import { ComponentProps, useMemo, useState } from 'react'
import classnames from 'classnames'

import ReactDatePicker, {
    ReactDatePickerCustomHeaderProps,
} from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'

import { Icon } from '@atoms'
import { IconButton } from '../IconButton/IconButton'

import './DatePicker.css'

type DatePickerProps = ComponentProps<typeof ReactDatePicker> & {
    requiredErrorMessage?: string
}
export const DatePicker: React.FC<DatePickerProps> = (props) => {
    const { className, requiredErrorMessage, ...rest } = props
    const { selected, required } = props

    const [touched, setTouched] = useState(false)
    const hasError = useMemo(
        () => !selected && touched && required,
        [required, selected, touched]
    )

    const datepickerClassName = classnames(
        'rounded-lg border border-neutral-200 !py-2 !pl-3 !pr-10 text-left font-inter text-sm leading-6',
        'outline outline-0 focus-within:outline-4 outline-white/0 transition-all',
        hasError
            ? 'focus-within:outline-red-500/25'
            : 'focus-within:border-azure focus-within:outline-azure/25',
        className,
        {
            'border-red-500': hasError,
        }
    )

    return (
        <div className="space-y-1">
            <ReactDatePicker
                onBlur={() => {
                    setTouched(true)
                }}
                className={datepickerClassName}
                shouldCloseOnSelect={false}
                showIcon={true}
                placeholderText="MM/DD/YYYY"
                showPopperArrow={false}
                renderCustomHeader={CustomHeader}
                icon={
                    <Icon
                        name="calendar_month"
                        size="small"
                        className="pointer-events-none text-neutral-800"
                    />
                }
                {...rest}
            />
            {hasError && requiredErrorMessage && (
                <div className="text-red-500">{requiredErrorMessage}</div>
            )}
        </div>
    )
}

const CustomHeader = ({
    date,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
}: ReactDatePickerCustomHeaderProps) => {
    const dayNames = ['S', 'M', 'T', 'W', 'T', 'F', 'S']
    return (
        <div>
            <div className="flex items-center justify-between">
                <IconButton
                    name="chevron-right"
                    size="xsmall"
                    iconSize="small"
                    icon="chevron_left"
                    variant="ghostPrimary"
                    className="text-neutral-800"
                    onClick={decreaseMonth}
                    disabled={prevMonthButtonDisabled}
                />

                <span className="text-center font-raleway text-sm font-bold leading-6">
                    {date.toLocaleString('default', {
                        month: 'long',
                        year: 'numeric',
                    })}
                </span>
                <IconButton
                    name="chevron-right"
                    size="xsmall"
                    iconSize="small"
                    icon="chevron_right"
                    variant="ghostPrimary"
                    className="text-neutral-800"
                    onClick={increaseMonth}
                    disabled={nextMonthButtonDisabled}
                />
            </div>
            <div className="grid grid-cols-7 py-4">
                {dayNames.map((day, index) => (
                    <span
                        key={index}
                        className="text-center font-raleway text-xs font-semibold leading-6 text-neutral-800"
                    >
                        {day}
                    </span>
                ))}
            </div>
        </div>
    )
}
